import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUser, updateUser } from '../store/slices/authSlice';
import PinModal from '../components/PinModal';
import { Toast } from '../components/Toast';

function Profile() {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const token = useSelector((state) => state.auth.token);
  const [activeTab, setActiveTab] = useState('created');
  const [selectedPinId, setSelectedPinId] = useState(null);
  
  // Data states
  const [userPins, setUserPins] = useState([]);
  const [savedPins, setSavedPins] = useState([]);
  const [likedPins, setLikedPins] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  
  // Profile edit states
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    bio: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Loading states
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Pagination states
  const [createdPage, setCreatedPage] = useState(1);
  const [savedPage, setSavedPage] = useState(1);
  const [likedPage, setLikedPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (currentUser) {
      loadUserData();
      loadUserPins();
      loadLikedPins();
      loadSavedPins();
      loadActivities();
      loadFollowStats();
    }
  }, [currentUser]);

  const loadUserData = async () => {
    setProfileData({
      displayName: currentUser.displayName || '',
      email: currentUser.email || '',
      bio: currentUser.bio || ''
    });
  };

  const loadUserPins = async () => {
    if (!currentUser?.username) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/pins/user/${currentUser.username}/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPins(data);
      }
    } catch (error) {
      console.error('Error loading user pins:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLikedPins = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/pins/liked/user/${currentUser.username}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLikedPins(data);
      }
    } catch (error) {
      console.error('Error loading liked pins:', error);
    }
  };

  const loadSavedPins = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/saved-pins/${currentUser.username}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedPins(data);
      }
    } catch (error) {
      console.error('Error loading saved pins:', error);
    }
  };

  const loadActivities = async () => {
    if (!currentUser?.username) return;
    
    // Only load activities if user is admin
    if (!currentUser?.isAdmin) {
      setActivities([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/activity-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          actor: currentUser.username,
          page: 1,
          limit: 50
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivities(data.logs || []);
      } else if (response.status === 403) {
        // User doesn't have permission, just set empty array
        setActivities([]);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
    }
  };

  const loadFollowStats = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile/${currentUser.username}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setFollowersCount(userData.followers?.length || 0);
        setFollowingCount(userData.following?.length || 0);
      }
    } catch (error) {
      console.error('Error loading follow stats:', error);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Toast.warning('Vui lòng chọn file ảnh!');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Toast.warning('Kích thước ảnh không được vượt quá 5MB!');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const { url } = await response.json();
        // Update profile with new avatar
        const updatedUser = await updateProfile({ avatar: url });
        if (updatedUser) {
          // Update Redux store with new avatar
          dispatch(updateUser({ avatar: url }));
          Toast.success('✨ Đã cập nhật avatar thành công!');
        }
      } else {
        Toast.error('❌ Lỗi khi tải ảnh lên!');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      Toast.error('❌ Lỗi khi tải ảnh lên!');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: currentUser.username,
          ...updates
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        // Update Redux store would go here
        return updatedUser;
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData.displayName.trim()) {
      Toast.warning('⚠️ Vui lòng nhập tên hiển thị!');
      return;
    }

    if (!profileData.email.trim()) {
      Toast.warning('⚠️ Vui lòng nhập email!');
      return;
    }

    setIsSavingProfile(true);
    try {
      const updatedUser = await updateProfile(profileData);
      if (updatedUser) {
        // Update Redux store with new profile data
        dispatch(updateUser(profileData));
        Toast.success('✨ Đã cập nhật thông tin thành công!');
      }
    } catch (error) {
      Toast.error('❌ Lỗi khi cập nhật thông tin!');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      Toast.warning('⚠️ Vui lòng nhập mật khẩu hiện tại!');
      return;
    }

    if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
      Toast.warning('⚠️ Mật khẩu mới phải có ít nhất 6 ký tự!');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Toast.warning('⚠️ Mật khẩu xác nhận không khớp!');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: currentUser.username,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        Toast.success('✨ Đã đổi mật khẩu thành công!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const error = await response.json();
        Toast.error(`❌ ${error.error || 'Lỗi khi đổi mật khẩu!'}`);
      }
    } catch (error) {
      Toast.error('❌ Lỗi khi đổi mật khẩu!');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Chờ duyệt', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      approved: { text: 'Đã duyệt', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      rejected: { text: 'Từ chối', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' }
    };
    return badges[status] || badges.pending;
  };

  const getPaginatedPins = (pins, page) => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return pins.slice(start, start + ITEMS_PER_PAGE);
  };

  const getTotalPages = (pins) => {
    return Math.ceil(pins.length / ITEMS_PER_PAGE);
  };

  const renderPins = (pins, page) => {
    const paginatedPins = getPaginatedPins(pins, page);
    
    if (paginatedPins.length === 0) {
      return (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <span className="material-symbols-rounded text-6xl text-gray-300 dark:text-gray-600 mb-4 block">
            {activeTab === 'created' ? 'add_circle' : activeTab === 'liked' ? 'favorite' : 'bookmark'}
          </span>
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {activeTab === 'created' ? 'Chưa có prompt nào' : 
             activeTab === 'liked' ? 'Chưa thích prompt nào' : 
             'Chưa lưu prompt nào'}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {paginatedPins.map((pin) => (
          <div key={pin._id} className="relative group">
            <div
              onClick={() => setSelectedPinId(pin._id)}
              className="cursor-pointer rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow bg-white dark:bg-gray-800"
            >
              <img
                src={pin.image}
                alt={pin.title}
                className="w-full h-auto object-cover aspect-square sm:aspect-auto"
              />
              <div className="p-2 sm:p-3">
                <h3 className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-white line-clamp-2">
                  {pin.title}
                </h3>
                {activeTab === 'created' && (
                  <div className="mt-2">
                    <span className={`inline-block px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${getStatusBadge(pin.status).class}`}>
                      {getStatusBadge(pin.status).text}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getActivityTitle = (type) => {
    const titles = {
      'user_created': 'Tạo tài khoản',
      'user_deleted': 'Xóa tài khoản',
      'user_made_admin': 'Được cấp quyền Admin',
      'pin_created': 'Tạo prompt mới',
      'pin_approved': 'Prompt được duyệt',
      'pin_rejected': 'Prompt bị từ chối',
      'pin_deleted': 'Xóa prompt',
      'comment_hidden': 'Ẩn bình luận',
      'comment_deleted': 'Xóa bình luận',
      'comment_violation': 'Bình luận vi phạm',
      'login': 'Đăng nhập',
      'logout': 'Đăng xuất',
      'view_user_profiles': 'Xem hồ sơ người dùng',
      'export_data': 'Xuất dữ liệu'
    };
    return titles[type] || type.replace(/_/g, ' ');
  };

  const renderActivities = () => {
    const paginatedActivities = getPaginatedPins(activities, historyPage);
    
    if (paginatedActivities.length === 0) {
      return (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <span className="material-symbols-rounded text-6xl text-gray-300 dark:text-gray-600 mb-4 block">
            history
          </span>
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            Chưa có hoạt động nào
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {paginatedActivities.map((activity) => (
          <div
            key={activity._id}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                activity.type.includes('create') ? 'bg-green-100 dark:bg-green-900' :
                activity.type.includes('update') || activity.type.includes('approved') ? 'bg-blue-100 dark:bg-blue-900' :
                activity.type.includes('delete') || activity.type.includes('rejected') ? 'bg-red-100 dark:bg-red-900' :
                activity.type.includes('login') || activity.type.includes('logout') ? 'bg-purple-100 dark:bg-purple-900' :
                activity.type.includes('comment') || activity.type.includes('violation') ? 'bg-orange-100 dark:bg-orange-900' :
                'bg-gray-100 dark:bg-gray-700'
              }`}>
                <span className={`material-symbols-rounded text-[20px] ${
                  activity.type.includes('create') ? 'text-green-600 dark:text-green-400' :
                  activity.type.includes('update') || activity.type.includes('approved') ? 'text-blue-600 dark:text-blue-400' :
                  activity.type.includes('delete') || activity.type.includes('rejected') ? 'text-red-600 dark:text-red-400' :
                  activity.type.includes('login') || activity.type.includes('logout') ? 'text-purple-600 dark:text-purple-400' :
                  activity.type.includes('comment') || activity.type.includes('violation') ? 'text-orange-600 dark:text-orange-400' :
                  'text-gray-600 dark:text-gray-400'
                }`}>
                  {activity.type.includes('create') ? 'add_circle' :
                   activity.type.includes('update') || activity.type.includes('approved') ? 'check_circle' :
                   activity.type.includes('delete') || activity.type.includes('rejected') ? 'cancel' :
                   activity.type.includes('login') ? 'login' :
                   activity.type.includes('logout') ? 'logout' :
                   activity.type.includes('comment') ? 'comment' :
                   activity.type.includes('admin') ? 'admin_panel_settings' :
                   'info'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  {getActivityTitle(activity.type)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {activity.target}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  {new Date(activity.createdAt).toLocaleString('vi-VN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPagination = (items, page, setPage) => {
    const totalPages = getTotalPages(items);
    if (totalPages <= 1) return null;

    return (
      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-2">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100 text-sm"
        >
          Trước
        </button>
        
        <div className="flex gap-2 overflow-x-auto max-w-full px-2 sm:px-0">
          {[...Array(Math.min(totalPages, 5))].map((_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (page <= 3) {
              pageNum = i + 1;
            } else if (page >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = page - 2 + i;
            }
            
            return (
              <button
                key={i}
                onClick={() => setPage(pageNum)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
                  page === pageNum
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100 text-sm"
        >
          Sau
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
      {/* Profile Header */}
      <div className="text-center mb-4 sm:mb-6 lg:mb-8">
        <div className="relative inline-block mb-3 sm:mb-4">
          <img
            src={currentUser?.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${currentUser?.username}&backgroundColor=e9e9e9`}
            alt={currentUser?.displayName}
            className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-full bg-gray-100 border-4 border-white dark:border-gray-700 shadow-lg"
          />
          {isUploadingAvatar && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
              <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <input
            type="file"
            id="avatar-upload"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarChange}
            disabled={isUploadingAvatar}
          />
          <button
            onClick={() => document.getElementById('avatar-upload').click()}
            disabled={isUploadingAvatar}
            className="absolute bottom-0 right-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-rounded text-white text-[18px] sm:text-[24px]">
              {isUploadingAvatar ? 'hourglass_empty' : 'edit'}
            </span>
          </button>
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 px-4">
          {currentUser?.displayName || currentUser?.username}
        </h1>
        <p className="text-xs sm:text-sm lg:text-base text-gray-500 dark:text-gray-400 mb-2 sm:mb-3 lg:mb-4">
          @{currentUser?.username}
        </p>
        {currentUser?.bio && (
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-300 max-w-md mx-auto mb-2 sm:mb-3 lg:mb-4 px-4">
            {currentUser.bio}
          </p>
        )}
        <div className="flex items-center justify-center gap-3 sm:gap-4 lg:gap-6 text-xs sm:text-sm flex-wrap px-4">
          <div>
            <span className="font-bold text-gray-900 dark:text-white">{userPins.length}</span>
            <span className="text-gray-500 dark:text-gray-400"> Prompts</span>
          </div>
          <div className="cursor-pointer hover:text-blue-600 transition-colors">
            <span className="font-bold text-gray-900 dark:text-white">{followersCount}</span>
            <span className="text-gray-500 dark:text-gray-400 hidden sm:inline"> Người theo dõi</span>
            <span className="text-gray-500 dark:text-gray-400 sm:hidden"> Followers</span>
          </div>
          <div className="cursor-pointer hover:text-blue-600 transition-colors">
            <span className="font-bold text-gray-900 dark:text-white">{followingCount}</span>
            <span className="text-gray-500 dark:text-gray-400 hidden sm:inline"> Đang theo dõi</span>
            <span className="text-gray-500 dark:text-gray-400 sm:hidden"> Following</span>
          </div>
          <div>
            <span className="font-bold text-gray-900 dark:text-white">{likedPins.length}</span>
            <span className="text-gray-500 dark:text-gray-400"> Đã thích</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4 sm:mb-6 overflow-x-auto">
        <div className="flex gap-2 sm:gap-4 lg:gap-8 justify-start sm:justify-center min-w-max px-2 sm:px-0">
          {[
            { id: 'created', label: 'Prompts của tôi', shortLabel: 'Của tôi' },
            { id: 'saved', label: 'Đã lưu', shortLabel: 'Đã lưu' },
            { id: 'liked', label: 'Đã thích', shortLabel: 'Đã thích' },
            { id: 'history', label: 'Hoạt động', shortLabel: 'Hoạt động' },
            { id: 'profile', label: 'Thông tin cá nhân', shortLabel: 'Cá nhân' },
            { id: 'password', label: 'Đổi mật khẩu', shortLabel: 'Mật khẩu' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 sm:pb-4 px-2 sm:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-xs sm:text-sm lg:text-base ${
                activeTab === tab.id
                  ? 'border-gray-900 dark:border-gray-100 text-gray-900 dark:text-white'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mb-8 sm:mb-12">
        {activeTab === 'created' && (
          <>
            {renderPins(userPins, createdPage)}
            {renderPagination(userPins, createdPage, setCreatedPage)}
          </>
        )}
        {activeTab === 'saved' && (
          <>
            {renderPins(savedPins, savedPage)}
            {renderPagination(savedPins, savedPage, setSavedPage)}
          </>
        )}
        {activeTab === 'liked' && (
          <>
            {renderPins(likedPins, likedPage)}
            {renderPagination(likedPins, likedPage, setLikedPage)}
          </>
        )}
        {activeTab === 'history' && (
          <>
            {renderActivities()}
            {renderPagination(activities, historyPage, setHistoryPage)}
          </>
        )}
        
        {/* Profile Settings Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in-up">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Thông tin cá nhân</h2>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span className="material-symbols-rounded text-[18px]">person</span>
                  Tên hiển thị
                </label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                  placeholder="Nhập tên hiển thị..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 transition-all text-sm sm:text-base hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span className="material-symbols-rounded text-[18px]">email</span>
                  Email
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 transition-all text-sm sm:text-base hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span className="material-symbols-rounded text-[18px]">description</span>
                  Giới thiệu
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  rows="3"
                  placeholder="Viết vài dòng về bạn..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 transition-all text-sm sm:text-base hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:pt-4">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  {isSavingProfile ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Đang lưu...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-rounded text-[20px]">save</span>
                      <span>Lưu thay đổi</span>
                    </>
                  )}
                </button>
                <button
                  onClick={loadUserData}
                  disabled={isSavingProfile}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-rounded text-[20px]">close</span>
                  <span>Hủy</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in-up">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Đổi mật khẩu</h2>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span className="material-symbols-rounded text-[18px]">lock</span>
                  Mật khẩu hiện tại
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Nhập mật khẩu hiện tại..."
                  disabled={isChangingPassword}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-purple-500 dark:focus:border-purple-500 transition-all text-sm sm:text-base hover:border-gray-400 dark:hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span className="material-symbols-rounded text-[18px]">lock_open</span>
                  Mật khẩu mới
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)..."
                  disabled={isChangingPassword}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-purple-500 dark:focus:border-purple-500 transition-all text-sm sm:text-base hover:border-gray-400 dark:hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {passwordData.newPassword && passwordData.newPassword.length < 6 && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <span className="material-symbols-rounded text-[14px]">info</span>
                    Mật khẩu cần ít nhất 6 ký tự
                  </p>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span className="material-symbols-rounded text-[18px]">check_circle</span>
                  Xác nhận mật khẩu mới
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Nhập lại mật khẩu mới..."
                  disabled={isChangingPassword}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-purple-500 dark:focus:border-purple-500 transition-all text-sm sm:text-base hover:border-gray-400 dark:hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                    <span className="material-symbols-rounded text-[14px]">error</span>
                    Mật khẩu xác nhận không khớp
                  </p>
                )}
                {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && passwordData.newPassword.length >= 6 && (
                  <p className="mt-2 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <span className="material-symbols-rounded text-[14px]">check_circle</span>
                    Mật khẩu khớp
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2 sm:pt-4">
                <button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword}
                  className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
                >
                  {isChangingPassword ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Đang đổi...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-rounded text-[20px]">lock_reset</span>
                      <span>Đổi mật khẩu</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pin Modal */}
      {selectedPinId && (
        <PinModal pinId={selectedPinId} onClose={() => setSelectedPinId(null)} />
      )}
    </div>
  );
}

export default Profile;
